import { jsPDF } from "jspdf";

export interface MedidaRelatorio {
  indice: number;
  referencia: boolean;
  texto: string;
}

export interface DadosRelatorioMedidas {
  paciente?: string;
  idade?: string;
  atendimento?: string | number;
  imagem: string; // dataURL jpeg da imagem com os traços
  medidas: MedidaRelatorio[];
  escala?: string;
}

const MARGEM = 15;
const LARGURA_PAGINA = 210;
const ALTURA_PAGINA = 297;

function carregarDimensoes(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => resolve({ width: 1, height: 1 });
    img.src = dataUrl;
  });
}

/**
 * Monta o relatório de medidas em PDF (1 página A4), no mesmo espírito dos
 * relatórios do CefX: cabeçalho com os dados do paciente, a imagem medida e a
 * relação das medidas obtidas.
 */
export async function montarPdfMedidas(dados: DadosRelatorioMedidas): Promise<Blob> {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const dim = await carregarDimensoes(dados.imagem);

  // o nome exibido na galeria vem do complemento cadastrado na clínica; o título
  // interno garante que o arquivo se identifique ao ser aberto
  doc.setProperties({
    title: dados.paciente
      ? `Relatório de Medidas - ${dados.paciente}`
      : "Relatório de Medidas",
    subject: "Medidas obtidas sobre a imagem do paciente",
    creator: "Serodonto",
  });

  let y = MARGEM;

  doc.setFontSize(16);
  doc.setTextColor(20, 20, 20);
  doc.text("Relatório de Medidas", MARGEM, y);
  y += 8;

  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);

  const cabecalho = [
    dados.paciente ? `Paciente: ${dados.paciente}` : null,
    dados.idade ? `Idade: ${dados.idade}` : null,
    dados.atendimento ? `Atendimento: ${dados.atendimento}` : null,
    `Emissão: ${new Date().toLocaleDateString("pt-BR")}`,
  ].filter(Boolean) as string[];

  cabecalho.forEach((linha) => {
    doc.text(linha, MARGEM, y);
    y += 5;
  });

  if (dados.escala) {
    doc.text(`Calibração: ${dados.escala}`, MARGEM, y);
    y += 5;
  }

  y += 3;
  doc.setDrawColor(200, 200, 200);
  doc.line(MARGEM, y, LARGURA_PAGINA - MARGEM, y);
  y += 6;

  // imagem ocupando a largura útil, preservando a proporção e sobrando espaço
  // para a relação de medidas
  const larguraUtil = LARGURA_PAGINA - MARGEM * 2;
  const alturaReservadaLista = Math.min(70, 14 + dados.medidas.length * 6);
  const alturaDisponivel = ALTURA_PAGINA - MARGEM - y - alturaReservadaLista;

  let larguraImg = larguraUtil;
  let alturaImg = (larguraUtil * dim.height) / dim.width;

  if (alturaImg > alturaDisponivel) {
    alturaImg = alturaDisponivel;
    larguraImg = (alturaImg * dim.width) / dim.height;
  }

  const xImg = MARGEM + (larguraUtil - larguraImg) / 2;
  doc.addImage(dados.imagem, "JPEG", xImg, y, larguraImg, alturaImg);
  y += alturaImg + 8;

  doc.setFontSize(12);
  doc.setTextColor(20, 20, 20);
  doc.text("Medidas obtidas", MARGEM, y);
  y += 6;

  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);

  if (dados.medidas.length === 0) {
    doc.text("Nenhuma medida registrada.", MARGEM, y);
  } else {
    dados.medidas.forEach((medida) => {
      if (y > ALTURA_PAGINA - MARGEM) {
        doc.addPage();
        y = MARGEM;
      }
      const rotulo = medida.referencia
        ? `Referência de calibração`
        : `Medida ${medida.indice}`;
      doc.text(`${rotulo}: ${medida.texto}`, MARGEM, y);
      y += 6;
    });
  }

  return doc.output("blob");
}

export function dataUrlParaBlob(dataUrl: string): Blob {
  const [cabecalho, base64] = dataUrl.split(",");
  const tipo = /:(.*?);/.exec(cabecalho)?.[1] || "image/jpeg";
  const binario = atob(base64);
  const bytes = new Uint8Array(binario.length);

  for (let i = 0; i < binario.length; i++) {
    bytes[i] = binario.charCodeAt(i);
  }

  return new Blob([bytes], { type: tipo });
}
